import React, { FocusEvent, ChangeEvent, useEffect, useState } from "react";
import { faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Alert,
  Badge,
  Button,
  Card,
  Dropdown,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import {
  selectSpitKitList,
  selectSpitKitListFilters,
  selectSpitKitListLoading,
  selectSpitKitListPagination,
  selectSpitKitSelection,
} from "src/store/SpitKit/SpitKitSelectors";
import {
  fullName,
  Pagination,
  RGCStatus,
  SpitKitExportFilters,
  SpitKitListFilters,
  SpitKitOrderEntry,
  SpitKitSelection,
} from "src/store/SpitKit/Types";
import { DEFAULT_SPIT_KIT_SELECTION } from "src/store/SpitKit/Constants";
import { utils } from "src/utils/utils_general";
import SortColumnHeader, {
  SortRequestedEvent,
} from "src/components/global/DataTable/SortColumnHeader";
// @ts-ignore
import { connect } from "react-redux";
import TablePagination from "src/components/global/DataTable/TablePagination";
import { OrderAction } from "./SpitKitActionsModal";
import classNames from "classnames";
import {
  doSetSpitKitListFilters,
  doSetSpitKitPagination,
  doSetSpitKitSelection,
} from "src/store/SpitKit/SpitKitActions";
import { DoDispatch } from "src/services/types/IStore";
import TableBody from "src/components/global/DataTable/TableBody";
import { SpitKitDate } from "./SpitKitDate";
// @ts-ignore
import SpitKitStatus from "./SpitKitStatus";
import { SpitKitReducerState } from "src/store/SpitKit/SpitKitReducer";
import { useInjection } from "src/services/ServicesContext";
import { ISpitKitService } from "src/services/SpitKitService";

import "./SpitKitTable.scss";

const TOTAL_COLUMNS = 17;
const SEARCH_DEBOUNCE_MS = 800;

export type SpitKitTableStateProps = Pick<
  SpitKitReducerState,
  | "spitKitListPagination"
  | "spitKitListFilters"
  | "spitKitList"
  | "spitKitListLoading"
  | "spitKitSelection"
>;

export type SpitKitTableDispatchProps = {
  setSpitKitListFilters: (filters: SpitKitListFilters) => void;
  setSpitKitListPagination: (pagination: Pagination) => void;
  setSpitKitSelection: (selection: SpitKitSelection) => void;
};

export type OrderActionRequest = {
  orderId: string;
  action: OrderAction;
};

export type BulkOrderActionRequest = {
  action: OrderAction;
};

export type SpitKitComponentProps = {
  onOrderActionRequested: ({ orderId, action }: OrderActionRequest) => void;
  onBulkActionRequested: (action: BulkOrderActionRequest) => void;
};

export type SpitKitProps = SpitKitComponentProps &
  SpitKitTableStateProps &
  RouteComponentProps &
  SpitKitTableDispatchProps;

function HeaderTooltip({ label, hint }: { label: string; hint: string }) {
  return (
    <OverlayTrigger
      placement="bottom"
      trigger={["hover", "focus"]}
      overlay={<Tooltip id={`spit-kit-header-${label}`}>{hint}</Tooltip>}
    >
      <span tabIndex={0} style={{ cursor: "help", display: "inline-block" }}>
        {label}
      </span>
    </OverlayTrigger>
  );
}

function UspsTrackingLink({ trackingNumber }: { trackingNumber?: string }) {
  if (!trackingNumber) return null;

  return (
    <a
      href={`https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${encodeURIComponent(
        trackingNumber,
      )}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      {trackingNumber}
    </a>
  );
}

function SpitKitTable({
  spitKitListPagination,
  spitKitListFilters,
  spitKitList,
  spitKitListLoading,
  spitKitSelection,
  setSpitKitListFilters,
  setSpitKitListPagination,
  setSpitKitSelection,
}: SpitKitProps) {
  const spitKitService = useInjection<ISpitKitService>("spitKitService");

  const {
    orderBy,
    orderByDirection,
    status,
    hasTrackingNumber,
    staleOutboundDays,
    staleReturnDays,
    searchTerm,
  } = spitKitListFilters;
  const { page, pageSize, resultCount, totalCount, totalPages } =
    spitKitListPagination;

  const initialActiveStaleFilter: "outbound" | "return" | null = (() => {
    if (staleOutboundDays != null) return "outbound";
    if (staleReturnDays != null) return "return";
    return null;
  })();
  const [activeStaleFilter, setActiveStaleFilter] = useState<
    "outbound" | "return" | null
  >(initialActiveStaleFilter);
  const [searchInput, setSearchInput] = useState(searchTerm ?? "");

  const matches = (...fields: (string | undefined)[]) => {
    if (!searchTerm) return false;
    const term = searchTerm.toLowerCase();
    return fields.some((value) => value?.toLowerCase().includes(term));
  };

  const highlight = (...fields: (string | undefined)[]) =>
    matches(...fields) ? "spit-kit-table-cell-match" : undefined;

  const filterKind = activeStaleFilter ?? "none";
  const filterValue = status || "";
  let filterDays: string | number = "";
  if (activeStaleFilter === "outbound" && staleOutboundDays != null) {
    filterDays = staleOutboundDays;
  } else if (activeStaleFilter === "return" && staleReturnDays != null) {
    filterDays = staleReturnDays;
  }

  const selectAll = spitKitSelection.selectAll;
  const selectedOrders = spitKitSelection.selectAll
    ? []
    : spitKitSelection.selectedOrders;
  const excludedOrderIds = spitKitSelection.selectAll
    ? spitKitSelection.excludedOrderIds
    : [];
  const selectedIds = new Set(selectedOrders.map(({ uuid }) => uuid));
  const excludedIds = new Set(excludedOrderIds);
  const isSelected = (uuid: string) =>
    selectAll ? !excludedIds.has(uuid) : selectedIds.has(uuid);
  const allPageSelected =
    spitKitList.length > 0 && spitKitList.every(({ uuid }) => isSelected(uuid));
  const selectedCount = selectAll
    ? totalCount - excludedOrderIds.length
    : selectedOrders.length;

  const setPage = (page: number) =>
    setSpitKitListPagination({
      ...spitKitListPagination,
      page: Math.min(page, Math.max(totalPages, 1)),
    });

  const updateFilters = (filters: SpitKitListFilters) => {
    if (selectAll) {
      setSpitKitSelection(DEFAULT_SPIT_KIT_SELECTION);
    }
    setSpitKitListFilters(filters);
    setSpitKitListPagination({
      ...spitKitListPagination,
      page: 1,
    });
  };

  const setExcludedOrderIds = (excludedOrderIds: string[]) => {
    if (excludedOrderIds.length >= totalCount) {
      setSpitKitSelection(DEFAULT_SPIT_KIT_SELECTION);
      return;
    }

    setSpitKitSelection({
      selectAll: true,
      excludedOrderIds,
    });
  };

  const setPageSelection = (selectedPageIds: string[]) => {
    const currentPageIds = spitKitList.map(({ uuid }) => uuid);

    if (selectAll) {
      setExcludedOrderIds([
        ...excludedOrderIds.filter((uuid) => !currentPageIds.includes(uuid)),
        ...spitKitList
          .filter(({ uuid }) => !selectedPageIds.includes(uuid))
          .map(({ uuid }) => uuid),
      ]);
      return;
    }

    setSpitKitSelection({
      selectAll: false,
      selectedOrders: [
        ...selectedOrders.filter(({ uuid }) => !currentPageIds.includes(uuid)),
        ...spitKitList.filter(({ uuid }) => selectedPageIds.includes(uuid)),
      ],
    });
  };

  const handleNext = () => setPage(Math.min(page + 1, totalPages));

  const handlePrev = () => setPage(Math.max(page - 1, 1));

  const handleSortRequested = ({
    columnName,
    currentSortOrder,
  }: SortRequestedEvent) => {
    let newOrderByDirection = orderByDirection;
    if (columnName === orderBy) {
      newOrderByDirection = currentSortOrder === "asc" ? "desc" : "asc";
    } else {
      newOrderByDirection = "asc";
    }

    updateFilters({
      orderBy: (columnName as keyof SpitKitOrderEntry) || "order_id",
      orderByDirection: newOrderByDirection,
    });
  };

  const clearSearch = () => {
    setSearchInput("");
    updateFilters({ searchTerm: undefined });
  };

  useEffect(() => {
    const next = searchInput.length > 0 ? searchInput : undefined;
    if (next === searchTerm) return;
    const timer = setTimeout(
      () => updateFilters({ searchTerm: next }),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchInput, searchTerm]);

  const handleStatusFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.currentTarget.value;
    updateFilters({
      status: value.length > 0 ? (value as RGCStatus) : undefined,
    });
  };

  const handleTrackingNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateFilters({
      hasTrackingNumber: e.currentTarget.checked || undefined,
    });
  };

  const setStaleDays = (days: number | undefined) => {
    if (activeStaleFilter === "outbound") {
      updateFilters({
        staleOutboundDays: days,
        staleReturnDays: undefined,
      });
    } else if (activeStaleFilter === "return") {
      updateFilters({
        staleOutboundDays: undefined,
        staleReturnDays: days,
      });
    }
  };

  const handleStaleFilterSelect = (nextFilterKind: "outbound" | "return") => {
    if (activeStaleFilter === nextFilterKind) return;
    setActiveStaleFilter(nextFilterKind);
    updateFilters({
      staleOutboundDays: undefined,
      staleReturnDays: undefined,
    });
  };

  const clearStaleFilter = () => {
    setActiveStaleFilter(null);
    updateFilters({
      staleOutboundDays: undefined,
      staleReturnDays: undefined,
    });
  };

  const handleStaleDaysChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    setStaleDays(value === "" ? undefined : Number(value));
  };

  const selectStaleDaysOnFocus = (e: FocusEvent<HTMLInputElement>) =>
    e.currentTarget.select();

  const togglePageSelection = (selected: boolean) => {
    setPageSelection(selected ? spitKitList.map(({ uuid }) => uuid) : []);
  };

  const handleSelectByStatus = (status: RGCStatus | "All" | "None") => {
    switch (status) {
      case "All": {
        togglePageSelection(true);
        return;
      }
      case "None": {
        togglePageSelection(false);
        return;
      }
      default: {
        setPageSelection(
          spitKitList
            .filter(({ status: orderStatus }) => orderStatus === status)
            .map(({ uuid }) => uuid),
        );
      }
    }
  };

  const handleRowToggle = (order: SpitKitOrderEntry) => {
    if (selectAll) {
      setExcludedOrderIds(
        isSelected(order.uuid)
          ? [...excludedOrderIds, order.uuid]
          : excludedOrderIds.filter((uuid) => uuid !== order.uuid),
      );
      return;
    }

    setSpitKitSelection({
      selectAll: false,
      selectedOrders: isSelected(order.uuid)
        ? selectedOrders.filter(({ uuid }) => uuid !== order.uuid)
        : [...selectedOrders, order],
    });
  };

  const handleExportSelected = async () => {
    if (selectedCount === 0) return;

    const filters: SpitKitExportFilters = selectAll
      ? {
          statuses: status ? [status] : [],
          searchTerm: spitKitListFilters.searchTerm,
          hasTrackingNumber,
          staleOutboundDays,
          staleReturnDays,
          excludedOrderIds,
        }
      : { uuids: selectedOrders.map(({ uuid }) => uuid) };

    const response = await spitKitService.exportSpitKitOrdersFile(filters);
    if (!response) return;
    window.open(response.signedUrl, "_blank");
  };

  const showSelectAllBanner =
    !selectAll && allPageSelected && totalCount > spitKitList.length;

  return (
    <>
      <Card>
        <Card.Header>
          <div className="d-flex align-items-center">
            <div className="mr-3 m-0 text-gray-600 d-flex align-items-center">
              {`Displaying ${resultCount} Order(s)`}
              {searchTerm && (
                <Badge
                  pill
                  className="ml-2 font-weight-normal"
                  style={{ backgroundColor: "#ede9fe", color: "#5b21b6" }}
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-1" />
                  matching “{searchTerm}”
                </Badge>
              )}
            </div>
            <div className="mr-3 d-flex align-items-center">
              <Dropdown className="d-inline mr-2">
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleSelectByStatus("All")}>
                    All
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSelectByStatus("None")}>
                    None
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleSelectByStatus("ordered")}
                  >
                    Ordered
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleSelectByStatus("pending")}
                  >
                    Pending
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleSelectByStatus("in_fulfillment")}
                  >
                    In Fulfillment
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => handleSelectByStatus("shipped")}
                  >
                    Shipped
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSelectByStatus("error")}>
                    Error
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <Form.Check
                type="checkbox"
                id="has-tracking-number-filter"
                label="Has tracking number"
                checked={Boolean(hasTrackingNumber)}
                onChange={handleTrackingNumberChange}
              />
            </div>
            <div className="mr-3">
              <Form.Control
                as="select"
                value={filterValue}
                onChange={handleStatusFilterChange}
                className={classNames("custom-select", {
                  "text-muted": !filterValue,
                })}
              >
                <option value={""}>Filter by Status</option>
                <option value={"ordered"}>Ordered</option>
                <option value={"pending"}>Pending</option>
                <option value={"in_fulfillment"}>In Fulfillment</option>
                <option value={"shipped"}>Shipped</option>
                <option value={"out_for_delivery"}>Out for Delivery</option>
                <option value={"delivered"}>Delivered</option>
                <option value={"collected"}>Collected</option>
                <option value={"returned"}>At Lab</option>
                <option value={"to_rgc"}>To RGC</option>
                <option value={"sequenced"}>Sequenced</option>
                <option value={"ancestry_available"}>Ancestry Available</option>
                <option value={"error"}>Error</option>
                <option value={"dnag_error"}>DNAG Error</option>
                <option value={"usps_error"}>Outbound USPS Error</option>
                <option value={"return_usps_error"}>Return USPS Error</option>
                <option value={"qc_failed"}>QC Failed</option>
              </Form.Control>
            </div>
            <div className="mr-3">
              <Button
                variant="primary"
                disabled={selectedCount === 0}
                onClick={handleExportSelected}
              >
                Export Selected
              </Button>
            </div>
            <div className="mr-3 mb-0">
              <InputGroup className="spit-kit-search">
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <Form.Control
                  placeholder="Name, email, address, barcode…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                />
                {searchInput && (
                  <InputGroup.Append>
                    <Button
                      variant="outline-secondary"
                      onClick={clearSearch}
                      aria-label="Clear search"
                    >
                      ×
                    </Button>
                  </InputGroup.Append>
                )}
              </InputGroup>
            </div>
          </div>
        </Card.Header>
        {showSelectAllBanner && (
          <Alert variant="info" className="m-0 rounded-0">
            All {spitKitList.length} rows on this page are selected.{" "}
            <Button
              variant="link"
              className="p-0"
              onClick={() => setExcludedOrderIds([])}
            >
              Select all {totalCount} orders matching this filter
            </Button>
          </Alert>
        )}
        {selectAll && (
          <Alert variant="info" className="m-0 rounded-0">
            All {selectedCount} orders matching this filter are selected.
            {excludedOrderIds.length > 0
              ? ` ${excludedOrderIds.length} order(s) excluded.`
              : ""}{" "}
            <Button
              variant="link"
              className="p-0"
              onClick={() => setSpitKitSelection(DEFAULT_SPIT_KIT_SELECTION)}
            >
              Clear selection
            </Button>
          </Alert>
        )}
        <Card.Body className="spit-kit-table-body p-0">
          <table className="table table-sm table-striped table-hover small">
            <thead>
              <tr>
                <th className="text-nowrap">
                  <Form.Check
                    type="checkbox"
                    id="select-all-visible"
                    aria-label="Select all visible rows"
                    checked={allPageSelected}
                    onChange={(e) =>
                      togglePageSelection(e.currentTarget.checked)
                    }
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Order ID"}
                    active={orderBy === "order_id"}
                    sortOrder={orderByDirection}
                    columnName={"order_id"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Name"}
                    active={orderBy === "first_name"}
                    sortOrder={orderByDirection}
                    columnName={"first_name"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">Address</th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Barcode"}
                    active={orderBy === "serial_id"}
                    sortOrder={orderByDirection}
                    columnName={"serial_id"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Kit Status"}
                    active={orderBy === "status"}
                    sortOrder={orderByDirection}
                    columnName={"status"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Order Kit Date"}
                    active={orderBy === "created"}
                    sortOrder={orderByDirection}
                    columnName={"created"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Received at Lab"}
                    hint="Date the kit was delivered back to the lab"
                    active={orderBy === "order_kit_to_lab_date"}
                    sortOrder={orderByDirection}
                    columnName={"order_kit_to_lab_date"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Sent to RGC"}
                    hint="Date the sample was sent to RGC for sequencing"
                    active={orderBy === "order_kit_to_rgc_date"}
                    sortOrder={orderByDirection}
                    columnName={"order_kit_to_rgc_date"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">
                  <HeaderTooltip
                    label="Ancestry"
                    hint="Whether the participant's ancestry results are available"
                  />
                </th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"Updated"}
                    active={orderBy === "updated"}
                    sortOrder={orderByDirection}
                    columnName={"updated"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="text-nowrap">OTN</th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"OTN Last Updated"}
                    active={orderBy === "outbound_updated_at"}
                    sortOrder={orderByDirection}
                    columnName={"outbound_updated_at"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="spit-kit-table-stale-filter-column">
                  <div className="d-flex flex-column align-items-start">
                    <div
                      className={classNames("d-flex align-items-center", {
                        "mb-1": filterKind === "outbound",
                      })}
                    >
                      <SortColumnHeader
                        text={"OTN Days Without Updates"}
                        active={orderBy === "outbound_updated_at"}
                        sortOrder={orderByDirection}
                        columnName={"outbound_updated_at"}
                        onSortRequested={handleSortRequested}
                      />
                      <Button
                        variant="link"
                        className={classNames(
                          "spit-kit-table-stale-filter-trigger p-0 ml-1",
                          {
                            active: filterKind === "outbound",
                          },
                        )}
                        onClick={() => handleStaleFilterSelect("outbound")}
                        aria-label="Filter outbound days without updates"
                        aria-pressed={filterKind === "outbound"}
                      >
                        <FontAwesomeIcon icon={faFilter} fixedWidth />
                      </Button>
                    </div>
                    {filterKind === "outbound" && (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          min={0}
                          value={filterDays}
                          onChange={handleStaleDaysChange}
                          onFocus={selectStaleDaysOnFocus}
                          aria-label="Outbound days without updates"
                          className="spit-kit-table-stale-filter-input mr-2"
                          autoFocus
                        />
                        <Button
                          variant="link"
                          className="spit-kit-table-stale-filter-clear p-0"
                          onClick={clearStaleFilter}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-nowrap">RTN</th>
                <th className="text-nowrap">
                  <SortColumnHeader
                    text={"RTN Last Updated"}
                    active={orderBy === "return_updated_at"}
                    sortOrder={orderByDirection}
                    columnName={"return_updated_at"}
                    onSortRequested={handleSortRequested}
                  />
                </th>
                <th className="spit-kit-table-stale-filter-column">
                  <div className="d-flex flex-column align-items-start">
                    <div
                      className={classNames("d-flex align-items-center", {
                        "mb-1": filterKind === "return",
                      })}
                    >
                      <SortColumnHeader
                        text={"RTN Days Without Updates"}
                        active={orderBy === "return_updated_at"}
                        sortOrder={orderByDirection}
                        columnName={"return_updated_at"}
                        onSortRequested={handleSortRequested}
                      />
                      <Button
                        variant="link"
                        className={classNames(
                          "spit-kit-table-stale-filter-trigger p-0 ml-1",
                          {
                            active: filterKind === "return",
                          },
                        )}
                        onClick={() => handleStaleFilterSelect("return")}
                        aria-label="Filter return days without updates"
                        aria-pressed={filterKind === "return"}
                      >
                        <FontAwesomeIcon icon={faFilter} fixedWidth />
                      </Button>
                    </div>
                    {filterKind === "return" && (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="number"
                          min={0}
                          value={filterDays}
                          onChange={handleStaleDaysChange}
                          onFocus={selectStaleDaysOnFocus}
                          aria-label="Return days without updates"
                          className="spit-kit-table-stale-filter-input mr-2"
                          autoFocus
                        />
                        <Button
                          variant="link"
                          className="spit-kit-table-stale-filter-clear p-0"
                          onClick={clearStaleFilter}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <TableBody
              isLoading={spitKitListLoading}
              columns={TOTAL_COLUMNS}
              rows={spitKitList.length}
              emptyMessage="No Orders Found"
            >
              {spitKitList.map((order) => {
                const {
                  uuid,
                  order_id,
                  serial_id,
                  status,
                  created,
                  updated,
                  tracking_number,
                  return_tracking_number,
                  outbound_updated_at,
                  return_updated_at,
                  order_kit_to_lab_date,
                  order_kit_to_rgc_date,
                  ancestry_available,
                  address_line1,
                  address_line2,
                  city,
                  state,
                  zip,
                  ...requester
                } = order;
                const requesterFullName = fullName(requester);
                const addressText = [
                  address_line1,
                  address_line2,
                  city,
                  [state, zip].filter(Boolean).join(" "),
                ]
                  .filter(Boolean)
                  .join(", ");
                const emailMatch = matches(requester.email);

                return (
                  <tr
                    key={uuid}
                    className={classNames({
                      "spit-kit-row-email-match": emailMatch,
                    })}
                  >
                    <td
                      className="align-middle"
                      title={
                        emailMatch ? "Email matches your search" : undefined
                      }
                    >
                      <Form.Check
                        type="checkbox"
                        id={`row-select-${uuid}`}
                        aria-label={`Select order ${order_id}`}
                        checked={isSelected(uuid)}
                        onChange={() => handleRowToggle(order)}
                      />
                    </td>
                    <td
                      className={classNames(
                        "align-middle text-nowrap",
                        highlight(order_id),
                      )}
                    >
                      {order_id}
                    </td>
                    <td
                      className={classNames(
                        "align-middle spit-kit-table-full-name-cell",
                        highlight(
                          requester.first_name,
                          requester.last_name,
                          requesterFullName,
                        ),
                      )}
                      title={requesterFullName}
                    >
                      {requesterFullName}
                    </td>
                    <td
                      className={classNames(
                        "align-middle text-nowrap",
                        highlight(
                          address_line1,
                          address_line2,
                          city,
                          state,
                          zip,
                        ),
                      )}
                      title={addressText}
                    >
                      {addressText}
                    </td>
                    <td
                      className={classNames(
                        "align-middle",
                        highlight(serial_id),
                      )}
                    >
                      {serial_id}
                    </td>
                    <td className="align-middle">
                      <SpitKitStatus status={status} />
                    </td>
                    <td className="align-middle">
                      <SpitKitDate date={created} />
                    </td>
                    <td className="align-middle">
                      <SpitKitDate date={order_kit_to_lab_date} />
                    </td>
                    <td className="align-middle">
                      <SpitKitDate date={order_kit_to_rgc_date} />
                    </td>
                    <td className="align-middle text-center">
                      {ancestry_available ? (
                        <span
                          aria-label="Ancestry available"
                          style={{ color: "#16a34a", fontWeight: 700 }}
                        >
                          ✓
                        </span>
                      ) : (
                        <span
                          aria-label="Ancestry not available"
                          className="text-muted"
                        >
                          —
                        </span>
                      )}
                    </td>
                    <td className="align-middle">
                      <SpitKitDate date={updated} />
                    </td>
                    <td className="align-middle">
                      <UspsTrackingLink trackingNumber={tracking_number} />
                    </td>
                    <td className="align-middle">
                      <SpitKitDate date={outbound_updated_at} />
                    </td>
                    <td className="align-middle">
                      {utils.daysSince(outbound_updated_at)}
                    </td>
                    <td className="align-middle">
                      <UspsTrackingLink
                        trackingNumber={return_tracking_number}
                      />
                    </td>
                    <td className="align-middle">
                      <SpitKitDate date={return_updated_at} />
                    </td>
                    <td className="align-middle">
                      {utils.daysSince(return_updated_at)}
                    </td>
                  </tr>
                );
              })}
            </TableBody>
          </table>
        </Card.Body>
      </Card>

      <TablePagination
        page={page}
        pageSize={pageSize}
        currentCount={resultCount}
        totalCount={totalCount}
        onPrev={handlePrev}
        onNext={handleNext}
      />
    </>
  );
}

const mapDispatchToProps = (
  dispatch: DoDispatch,
): SpitKitTableDispatchProps => ({
  setSpitKitListFilters: (filters: SpitKitListFilters) =>
    dispatch(doSetSpitKitListFilters(filters)),
  setSpitKitListPagination: (pagination: Pagination) =>
    dispatch(doSetSpitKitPagination(pagination)),
  setSpitKitSelection: (selection: SpitKitSelection) =>
    dispatch(doSetSpitKitSelection(selection)),
});

const mapStateToProps = createStructuredSelector<SpitKitTableStateProps>({
  spitKitListPagination: selectSpitKitListPagination,
  spitKitListFilters: selectSpitKitListFilters,
  spitKitList: selectSpitKitList,
  spitKitListLoading: selectSpitKitListLoading,
  spitKitSelection: selectSpitKitSelection,
});

export default connect(
  mapStateToProps,
  // TODO: fix types
  // @ts-ignore
  mapDispatchToProps,
)(withRouter(SpitKitTable));
